import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { toValidObjectIdString } from '../utils/security';
import { formatMesafeKm } from '../utils/mesafeFormat';
import './RandevuBedeliDetayModal.css';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '—';
  return `${parseFloat(amount).toFixed(2)} ₺`;
};

const DURUM_LABELS = {
  OnayBekliyor: 'Onay Bekliyor',
  Onaylandi: 'Onaylandı',
  HakediseAktarildi: 'Hakedişe Aktarıldı',
  IptalEdildi: 'İptal Edildi',
};

const SectionCard = ({ title, children }) => (
  <section className="rb-detay-section">
    <h3 className="rb-detay-section-title">{title}</h3>
    {children}
  </section>
);

const MoneyTable = ({ columns, rows, emptyText }) => {
  if (!rows.length) {
    return <div className="rb-detay-empty">{emptyText}</div>;
  }
  return (
    <div className="rb-detay-table-wrap">
      <table className="admins-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={col.style}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>{row.cells}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DescCell = ({ name, code, aciklama }) => (
  <td className="rb-detay-desc-cell">
    <div className="rb-detay-name">{name || '—'}</div>
    {code && <div className="rb-detay-code">Kod: {code}</div>}
    {aciklama && <div className="rb-detay-aciklama">{aciklama}</div>}
  </td>
);

/**
 * Randevu bedeli detay modalı — işlem, ilave işlem ve yol bedeli bölümleri.
 */
const RandevuBedeliDetayModal = ({ bedel, onClose, fetchDetay }) => {
  const [detay, setDetay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const id = toValidObjectIdString(bedel?.id ?? bedel?._id);
    if (!id || !fetchDetay) {
      setDetay(bedel);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    fetchDetay(id)
      .then((res) => {
        if (res?.basarili && res?.veri?.randevuBedeli) {
          setDetay(res.veri.randevuBedeli);
        } else {
          setDetay(bedel);
        }
      })
      .catch(() => {
        const hasFallback = Array.isArray(bedel?.satirlar) && bedel.satirlar.length > 0;
        setError(hasFallback ? '' : 'Randevu bedeli detayı yüklenemedi');
        setDetay(bedel);
      })
      .finally(() => setLoading(false));
  }, [bedel, fetchDetay]);

  if (!bedel) return null;

  const data = detay || bedel;
  const satirlar = data.satirlar || [];
  const islemler = satirlar.filter((s) => s.tip === 'islem');
  const ilaveIslemler = satirlar.filter((s) => s.tip === 'ilave');

  const yolBedeli = parseFloat(data.yolBedeli) || 0;
  const mesafe = parseFloat(data.mesafe) || 0;
  const mesafeGidisDonus = data.mesafeGidisDonus === true;
  const birimYolBedeli = mesafe > 0 ? yolBedeli / mesafe : 0;

  const randevuNumarasi = data.randevuNumarasi || data.randevu?.randevuNumarasi || '—';
  const teknikServisAdi = data.teknikServisAdi || data.teknikServis?.ad || '—';
  const servisKaydiId = data.servisKaydiId || (data.randevu?.servisKaydiId && String(data.randevu.servisKaydiId));
  const randevuId = data.randevuId || (data.randevu?._id && String(data.randevu._id));
  const randevuLinkHref = servisKaydiId && randevuId
    ? `/service-records/${servisKaydiId}/appointments/${randevuId}`
    : null;

  const durum = data.durum || '';
  const durumClass = durum === 'Onaylandi'
    ? 'onaylandi'
    : durum === 'HakediseAktarildi'
      ? 'hakedise-aktarildi'
      : durum === 'IptalEdildi'
        ? 'iptal-edildi'
        : 'onay-bekliyor';

  const moneyColumns = [
    { key: 'desc', label: 'Açıklama / Kod', style: { minWidth: '180px' } },
    { key: 'qty', label: 'Adet', style: { width: '72px', textAlign: 'center' } },
    { key: 'price', label: 'Fiyat', style: { width: '100px', textAlign: 'right' } },
    { key: 'total', label: 'Toplam', style: { width: '100px', textAlign: 'right' } },
  ];

  const buildMoneyRow = (s, idx, prefix) => {
    const name = (s.islemAdi || s.parcaAdi || s.aciklama || '—').trim();
    const code = (s.islemKodu || s.parcaKodu || '').trim() || null;
    const aciklama = s.tip === 'ilave' ? (s.aciklama || '').trim() || null : null;
    const satirToplam = (parseFloat(s.fiyat) || 0) * (parseInt(s.adet, 10) || 1);
    return {
      key: `${prefix}-${idx}`,
      cells: (
        <>
          <DescCell name={name} code={code} aciklama={aciklama} />
          <td style={{ textAlign: 'center' }}>{s.adet ?? 1}</td>
          <td style={{ textAlign: 'right' }}>{formatCurrency(s.fiyat)}</td>
          <td style={{ textAlign: 'right', fontWeight: 500 }}>{formatCurrency(satirToplam)}</td>
        </>
      ),
    };
  };

  const modal = (
    <div
      className="modal-overlay"
      style={{ zIndex: 10500 }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="randevu-bedeli-detay-title"
    >
      <div
        className="modal-content randevu-bedeli-detay-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="randevu-bedeli-detay-title">Randevu Bedeli Detayı</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="rb-detay-loading">
              <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
              {' '}Yükleniyor…
            </div>
          ) : (
            <>
              {error && <div className="rb-detay-error">{error}</div>}

              <div className="rb-detay-summary">
                <div className="rb-detay-summary-item">
                  <span className="rb-detay-summary-label">Randevu No</span>
                  <span className="rb-detay-summary-value">{randevuNumarasi}</span>
                </div>
                <div className="rb-detay-summary-item">
                  <span className="rb-detay-summary-label">Teknik Servis</span>
                  <span className="rb-detay-summary-value">{teknikServisAdi}</span>
                </div>
                <div className="rb-detay-summary-item">
                  <span className="rb-detay-summary-label">Toplam Bedel</span>
                  <span className="rb-detay-summary-value rb-detay-total">{formatCurrency(data.toplamTutar)}</span>
                </div>
                <div className="rb-detay-summary-actions">
                  <span className={`rb-detay-status-badge ${durumClass}`}>
                    {DURUM_LABELS[durum] || durum || '—'}
                  </span>
                  {randevuLinkHref && (
                    <Link to={randevuLinkHref} className="rb-detay-randevu-link">
                      <i className="fas fa-external-link-alt" aria-hidden="true"></i>
                      İlgili randevu
                    </Link>
                  )}
                </div>
              </div>

              <SectionCard title="İşlem">
                <MoneyTable
                  columns={moneyColumns}
                  rows={islemler.map((s, idx) => buildMoneyRow(s, idx, 'islem'))}
                  emptyText="İşlem yok"
                />
              </SectionCard>

              <SectionCard title="İlave işlemler">
                <MoneyTable
                  columns={moneyColumns}
                  rows={ilaveIslemler.map((s, idx) => buildMoneyRow(s, idx, 'ilave'))}
                  emptyText="İlave işlem yok"
                />
              </SectionCard>

              <SectionCard title="Yol bedeli">
                {mesafe > 0 || yolBedeli > 0 ? (
                  <div className="rb-detay-table-wrap">
                    <table className="admins-table">
                      <thead>
                        <tr>
                          <th style={{ minWidth: '180px' }}>Açıklama</th>
                          <th style={{ width: '80px', textAlign: 'center' }}>{mesafeGidisDonus ? 'KM (Gidiş-Dönüş)' : 'KM'}</th>
                          <th style={{ width: '100px', textAlign: 'right' }}>Birim fiyat</th>
                          <th style={{ width: '100px', textAlign: 'right' }}>Toplam</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="rb-detay-desc-cell">
                            <div className="rb-detay-name">Yol bedeli</div>
                          </td>
                          <td style={{ textAlign: 'center' }}>{mesafe > 0 ? formatMesafeKm(mesafe, mesafeGidisDonus) : '—'}</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(birimYolBedeli)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 500 }}>{formatCurrency(yolBedeli)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="rb-detay-empty">Yol bedeli yok</div>
                )}
              </SectionCard>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="secondary-btn" onClick={onClose}>Kapat</button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default RandevuBedeliDetayModal;
